import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { atom, useAtom } from 'jotai';
import merge from 'lodash/merge';

type Parameter = {
  scope: string;
  key: string;
  value: string | null;
};

// TODO find how to do the tests without exporting this
export const paramsAtom = atom<Record<string, Parameter>>({});

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryState(
  name: string,
  def?: string | (() => string),
  options?: { keepHistory?: boolean; scope?: string; enforceNotNull?: boolean }
): [
  string | null,
  (value: string | null | ((prev: string | null) => string | null)) => void,
] {
  const keepHistory = options?.keepHistory ?? true;
  const scope = options?.scope ?? 'global';
  const scopedName = `${scope}_${name}`;
  const returnDefault = React.useRef(true);
  const lastValueSet = React.useRef<string | null>(undefined);
  const [params, setParams] = useAtom(paramsAtom);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const defValue = React.useMemo(
    () => (typeof def === 'function' ? def() : (def ?? null)),
    [def]
  );
  const query = new URLSearchParams(search).get(name);

  // Set the new desired value in the params atom
  const setQuery = React.useCallback(
    (
      value: string | null | ((prev: string | null) => string | null),
      keepExisting: boolean = false
    ) => {
      setParams((params) => {
        // if we had a value in state from previous history and we want to keep it, dont return def
        if (keepExisting && params[scopedName]) return params;
        // return the same object to bail out if there are no changes to apply
        const newValue =
          typeof value === 'function'
            ? value(params[scopedName]?.value)
            : value;
        if (newValue === params[scopedName]?.value) return params;
        else {
          // mark the old value as set (in case it had been an old state value, not set by us)
          lastValueSet.current = params[scopedName]?.value;
          return {
            ...params,
            [scopedName]: { scope: scope, key: name, value: newValue },
          };
        }
      });
    },
    [name, scope, scopedName, setParams]
  );

  // when the search string changes, update the params atom with the new values
  React.useEffect(() => {
    // if we were given a default value && we still haven't set a default value,
    // or if we were told to enforce not null and we have a null query,
    // set the default value in the query string
    if (query === null && (returnDefault.current || options?.enforceNotNull)) {
      setQuery(defValue, keepHistory);
    } else {
      // else, the query just changed, set it in the params state object
      setQuery(query, false);
      returnDefault.current = false;
    }
  }, [defValue, keepHistory, options?.enforceNotNull, query, setQuery]);

  // when params atom changes, update the query string
  React.useEffect(() => {
    // if we already have this value in the query, or if it didnt change, dont set it again,
    // unless we dont have a query, and we want to enforce not null queries, then always do it.
    if (
      !(options?.enforceNotNull && query === null) &&
      (params[scopedName]?.value === lastValueSet.current ||
        params[scopedName]?.value === query)
    )
      return;
    lastValueSet.current = params[scopedName]?.value;

    function isNotNullTuple(a: [string, string | null]): a is [string, string] {
      return a[1] !== null;
    }
    const queryKeyval = search
      .replace('?', '')
      .split('&')
      .filter((keyval) => keyval !== '')
      .map((keyval) => keyval.split('='))
      .reduce<Record<string, string>>(
        (keyval, [key, val]) => ({ ...keyval, [key]: val ?? '@@no_val' }), //if there was an existing param with no value, mark it, and we'll restore it below
        {}
      );
    const globalKeyval = Object.values(params)
      .filter((param) => Object.keys(queryKeyval).includes(param.key))
      .reduce<Record<string, string | null>>(
        (keyval, { scope: paramScope, key, value }) =>
          paramScope !== 'global' ? keyval : { ...keyval, [key]: value },
        {}
      );
    const scopeKeyval = Object.values(params).reduce<
      Record<string, string | null>
    >(
      (keyval, { scope: paramScope, key, value }) =>
        paramScope !== scope ? keyval : { ...keyval, [key]: value },
      {}
    );
    const finalKeyval = merge(queryKeyval, globalKeyval, scopeKeyval);
    let newSearch =
      '?' +
      Object.entries(finalKeyval)
        .filter(isNotNullTuple)
        .map(([key, value]) => (value === '@@no_val' ? key : `${key}=${value}`))
        .join('&');

    // if we use push to go from null query to the desired query, the back button will take us back to null query,
    //and the hook will change it again to the desired value, so we can't navigate back... use replace instead
    options?.enforceNotNull && query === null
      ? history.replace(pathname.concat(newSearch, window.location.hash))
      : history.push(pathname.concat(newSearch, window.location.hash));
  }, [
    history,
    options?.enforceNotNull,
    params,
    pathname,
    query,
    scope,
    scopedName,
    search,
  ]);

  return [
    query === null && returnDefault.current
      ? defValue
      : query === null && options?.enforceNotNull
        ? params[scopedName].value
        : query,
    setQuery,
  ];
}

export const useThrottledQueryState = (
  name: string,
  def?: string | (() => string),
  options?: {
    keepHistory?: boolean;
    scope?: string;
    enforceNotNull?: boolean;
    timeout?: number;
  }
) => {
  const timeout = options?.timeout ?? 500;
  const [throttledValue, setThrottledValue] = useQueryState(name, def, options);
  const [value, setValue] = React.useState<string | null>(throttledValue);
  const lastRan = React.useRef(Date.now());

  React.useEffect(() => {
    const handler = setTimeout(
      function () {
        if (Date.now() - lastRan.current >= timeout) {
          setThrottledValue(value);
          lastRan.current = Date.now();
        }
      },
      timeout - (Date.now() - lastRan.current)
    );

    return () => {
      clearTimeout(handler);
    };
  }, [value, timeout, setThrottledValue]);

  return [value, setValue, throttledValue] as const;
};

export function useQueryStateBool(
  name: string
): [boolean, (value: boolean | ((prev: boolean) => boolean)) => void] {
  const { search, pathname, hash } = useLocation();
  const history = useHistory();
  // if the param name is in the query, but no value is given, result is '' but treat it as true
  const query = ['true', ''].includes(
    new URLSearchParams(search).get(name) ?? '@#not_included@#' //this is to make the type checker happy, we cannot check Array[string].includes of something that might be nullish
  );
  const setQuery = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(query) : value;
    if (query === newValue) return;
    const newSearch = search
      .replace('?', '')
      .split('&')
      .filter((p) => p && !new RegExp(`\\??${name}(=.*)?`).test(p))
      .concat(newValue ? `&${name}=${newValue}` : '')
      .join('&');
    history.replace(
      pathname
        .replace(/\?[^#]*/, '')
        .concat('?')
        .concat(newSearch) + hash
    );
  };
  return [query, setQuery];
}

export function useHashStateBool(
  name: string,
  mode: 'replace' | 'push' = 'replace'
): [
  boolean,
  (value: boolean | ((prev: boolean) => boolean), state?: unknown) => void,
] {
  const { hash } = useLocation();
  const history = useHistory();
  // if the param name is in the query, but no value is given, result is '' but treat it as true
  const query = ['true', ''].includes(
    new URLSearchParams(hash.substr(1)).get(name) ?? '@#not_included@#' //this is to make the type checker happy, we cannot check Array[string].includes of something that might be nullish
  );
  const setQuery = (
    value: boolean | ((prev: boolean) => boolean),
    state?: unknown
  ) => {
    const newValue = typeof value === 'function' ? value(query) : value;
    const newHash = hash
      .replace('#', '')
      .split('&')
      .filter((p) => p && !new RegExp(`#?${name}(=.*)?`).test(p))
      .concat(newValue ? name : '')
      .join('&');
    history[mode](
      window.location.pathname.replace(/#.*/, '').concat('#').concat(newHash),
      state
    );
  };
  return [query, setQuery];
}
